import {
    ActionIcon,
    Badge,
    Box,
    Button,
    Group,
    Menu,
    NumberInput,
    Paper,
    Stack,
    Text,
    Textarea,
} from '@mantine/core'
import {IconChevronDown, IconGripVertical, IconPlus} from '@tabler/icons-react'
import clsx from 'clsx'
import {useMemo, useState} from 'react'

import {formatPrice} from 'src/lib/format-price'
import {useTransportationRatePolicy} from 'src/settings/use-transportation-rate-policy'

import {
    addDisposalLine,
    addHseiLine,
    addItemLine,
    addPercentLine,
    addTransportationLine,
    addTransportationPerLoadLine,
    addWashoutLine,
    type ChargeType,
    chargeTypeAvailable,
    isRentalPeriodUnit,
    isValidRentalPeriod,
    itemSubtotal,
    lineExtended,
    linePeriodCount,
    lineRatesHint,
    moveLine,
    QUOTE_UNIT_META,
    QUOTE_UNITS,
    type QuoteItemLine,
    type QuoteLine,
    type QuoteRentalPeriod,
    quoteSubtotal,
    type QuoteUnit,
    removeLine,
    rentalPeriodBasis,
    unitSingularLabel,
    updateLine,
} from './quote-builder-logic'
import classes from './quote-line-items.module.css'
import {QuoteLinePeriodBasis} from './quote-line-period-basis'

type QuoteLineItemsProps = {
    lines: QuoteLine[]
    onLinesChange: (lines: QuoteLine[]) => void
    readOnly?: boolean
    rentalPeriod?: QuoteRentalPeriod | null
}

const CHARGE_TYPE_LABELS: Record<ChargeType, string> = {
    disposal: 'Disposal',
    washout: 'Washout',
    hsei: 'HSEI',
    transportation: 'Transportation',
}

const CHARGE_TYPE_MENU_ITEMS: {
    chargeType: ChargeType
    label: string
    hint: string
    onAdd: (lines: QuoteLine[], defaults: {transportationHourlyRate: number}) => QuoteLine[]
    usesDefaultHourlyRate?: boolean
}[] = [
    {
        chargeType: 'transportation',
        label: 'Transportation',
        hint: 'hourly',
        onAdd: (lines, {transportationHourlyRate}) =>
            addTransportationLine(lines, transportationHourlyRate),
        usesDefaultHourlyRate: true,
    },
    {
        chargeType: 'transportation',
        label: 'Transportation per load',
        hint: 'per load',
        onAdd: addTransportationPerLoadLine,
    },
    {
        chargeType: 'disposal',
        label: 'Disposal fee',
        hint: 'gal · bbl · ton',
        onAdd: addDisposalLine,
    },
    {chargeType: 'washout', label: 'Washout', hint: 'flat', onAdd: addWashoutLine},
    {chargeType: 'hsei', label: 'HSEI', hint: '% of subtotal', onAdd: addHseiLine},
]

export function QuoteLineItems({
    lines,
    onLinesChange,
    readOnly = false,
    rentalPeriod = null,
}: QuoteLineItemsProps) {
    const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
    const [rateDrafts, setRateDrafts] = useState<Record<string, string>>({})
    const hasRentalPeriod = isValidRentalPeriod(rentalPeriod)

    const itemTotal = useMemo(() => itemSubtotal(lines, rentalPeriod), [lines, rentalPeriod])
    const subtotal = useMemo(() => quoteSubtotal(lines, rentalPeriod), [lines, rentalPeriod])
    const ratesHint = useMemo(() => lineRatesHint(lines, rentalPeriod), [lines, rentalPeriod])
    const {data: transportationRatePolicy} = useTransportationRatePolicy()

    function addCommonCharge({
        onAdd,
        usesDefaultHourlyRate,
    }: (typeof CHARGE_TYPE_MENU_ITEMS)[number]) {
        const transportationHourlyRate = usesDefaultHourlyRate
            ? (transportationRatePolicy?.defaultHourlyRate ?? 0)
            : 0
        onLinesChange(onAdd(lines, {transportationHourlyRate}))
    }

    function handleUnitChange(line: QuoteItemLine, unit: QuoteUnit) {
        if (!hasRentalPeriod) {
            onLinesChange(updateLine(lines, line.id, {unit}))
            return
        }
        const forRentalPeriod = isRentalPeriodUnit(unit)
        onLinesChange(
            updateLine(lines, line.id, {
                unit,
                forRentalPeriod,
                duration: forRentalPeriod ? null : line.duration,
            }),
        )
    }

    function handleRateChange(lineId: string, value: number | string) {
        const draft = typeof value === 'number' ? String(value) : value
        setRateDrafts((current) => ({...current, [lineId]: draft}))

        if (typeof value === 'number') {
            onLinesChange(updateLine(lines, lineId, {rate: value}))
        }
    }

    function handleRateBlur(lineId: string) {
        const draft = rateDrafts[lineId]
        if (draft == null) {
            return
        }

        const parsedRate = Number(draft)
        onLinesChange(
            updateLine(lines, lineId, {
                rate: Number.isFinite(parsedRate) && parsedRate >= 0 ? parsedRate : 0,
            }),
        )
        setRateDrafts((current) => {
            const next = {...current}
            delete next[lineId]
            return next
        })
    }

    function handleAddItemLine() {
        const nextLines = addItemLine(lines)
        const addedLine = nextLines[nextLines.length - 1]
        if (addedLine?.kind === 'item') {
            setRateDrafts((current) => ({...current, [addedLine.id]: ''}))
        }
        onLinesChange(nextLines)
    }

    function handleAddPercentLine() {
        onLinesChange(addPercentLine(lines))
    }

    function handleDrop(targetIndex: number) {
        if (draggedIndex == null) {
            return
        }

        onLinesChange(moveLine(lines, draggedIndex, targetIndex))
        setDraggedIndex(null)
    }

    return (
        <Paper withBorder radius="md" p="md">
            <Stack gap="md">
                <Group justify="space-between" align="flex-start">
                    <Text fw={600}>Line items</Text>
                    <Text size="xs" c="dimmed" ta="right" maw={420}>
                        {ratesHint}
                    </Text>
                </Group>

                <Box style={{overflowX: 'auto'}}>
                    <div className={classes['headerRow']}>
                        <span aria-hidden="true" />
                        <span>QTY</span>
                        <span>Description</span>
                        <span>Duration</span>
                        <span>Rate</span>
                        <span>{hasRentalPeriod ? 'Basis' : ''}</span>
                        <span className={classes['extHeader']}>Ext $</span>
                        <span aria-hidden="true" />
                    </div>

                    {lines.map((line, index) => {
                        const extended = lineExtended(line, itemTotal, rentalPeriod)
                        const periodBasis =
                            line.kind === 'item' && hasRentalPeriod && isRentalPeriodUnit(line.unit)
                                ? rentalPeriodBasis(line.unit, rentalPeriod)
                                : null
                        const periodCount = linePeriodCount(line, rentalPeriod)

                        return (
                            <div
                                key={line.id}
                                className={clsx(
                                    classes['lineRow'],
                                    draggedIndex === index && classes['lineRowDragging'],
                                )}
                                onDragOver={(event) => event.preventDefault()}
                                onDrop={() => !readOnly && handleDrop(index)}
                            >
                                <button
                                    type="button"
                                    className={classes['gripHandle']}
                                    aria-label="Reorder line"
                                    draggable={!readOnly}
                                    onDragStart={() => !readOnly && setDraggedIndex(index)}
                                    onDragEnd={() => setDraggedIndex(null)}
                                >
                                    <IconGripVertical size={16} />
                                </button>

                                {line.kind === 'item' ? (
                                    <div className={classes['quantityCell']}>
                                        <NumberInput
                                            size="sm"
                                            aria-label="Line quantity"
                                            min={0}
                                            decimalScale={2}
                                            value={line.quantity}
                                            readOnly={readOnly}
                                            onChange={(value) =>
                                                onLinesChange(
                                                    updateLine(lines, line.id, {
                                                        quantity:
                                                            typeof value === 'number' ? value : 0,
                                                    }),
                                                )
                                            }
                                            classNames={{root: classes['quantityInput']}}
                                        />
                                    </div>
                                ) : (
                                    <Text size="sm" c="dimmed">
                                        —
                                    </Text>
                                )}

                                <Group gap={6} wrap="nowrap" style={{minWidth: 0}}>
                                    {line.chargeType && (
                                        <Badge size="xs" variant="light" style={{flex: 'none'}}>
                                            {CHARGE_TYPE_LABELS[line.chargeType]}
                                        </Badge>
                                    )}
                                    <Textarea
                                        size="sm"
                                        aria-label="Line description"
                                        value={line.description}
                                        autosize
                                        minRows={1}
                                        readOnly={readOnly}
                                        style={{flex: 1, minWidth: 0}}
                                        onChange={(event) =>
                                            onLinesChange(
                                                updateLine(lines, line.id, {
                                                    description: event.currentTarget.value,
                                                }),
                                            )
                                        }
                                    />
                                </Group>

                                {line.kind === 'item' ? (
                                    <>
                                        <div className={classes['quantityCell']}>
                                            <NumberInput
                                                size="sm"
                                                aria-label="Line duration"
                                                title={
                                                    periodCount != null
                                                        ? 'Set by the rental period'
                                                        : undefined
                                                }
                                                min={0}
                                                decimalScale={2}
                                                value={periodCount ?? line.duration ?? ''}
                                                readOnly={readOnly || periodCount != null}
                                                onChange={(value) =>
                                                    onLinesChange(
                                                        updateLine(lines, line.id, {
                                                            duration:
                                                                typeof value === 'number'
                                                                    ? value
                                                                    : null,
                                                        }),
                                                    )
                                                }
                                                classNames={{root: classes['quantityInput']}}
                                            />
                                        </div>

                                        <div className={classes['rateCell']}>
                                            <Text size="sm" c="dimmed">
                                                $
                                            </Text>
                                            <NumberInput
                                                size="sm"
                                                aria-label="Line rate"
                                                min={0}
                                                allowDecimal
                                                decimalScale={2}
                                                fixedDecimalScale={rateDrafts[line.id] == null}
                                                step={0.01}
                                                value={rateDrafts[line.id] ?? line.rate}
                                                readOnly={readOnly}
                                                onChange={(value) =>
                                                    handleRateChange(line.id, value)
                                                }
                                                onBlur={() => handleRateBlur(line.id)}
                                                classNames={{root: classes['rateInput']}}
                                            />
                                            <Text
                                                size="sm"
                                                c="dimmed"
                                                className={classes['rateUnit']}
                                            >
                                                /
                                            </Text>
                                            <div className={classes['unitSelectWrapper']}>
                                                <Text
                                                    size="sm"
                                                    className={classes['unitSelectLabel']}
                                                >
                                                    {unitSingularLabel(line.unit)}▾
                                                </Text>
                                                <select
                                                    aria-label="Line unit"
                                                    className={classes['unitSelectOverlay']}
                                                    value={line.unit}
                                                    disabled={readOnly}
                                                    onChange={(event) =>
                                                        handleUnitChange(
                                                            line,
                                                            event.currentTarget.value as QuoteUnit,
                                                        )
                                                    }
                                                >
                                                    {QUOTE_UNITS.map((unit) => (
                                                        <option key={unit} value={unit}>
                                                            {QUOTE_UNIT_META[unit].label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <Text size="sm" c="dimmed">
                                            —
                                        </Text>
                                        <div className={classes['rateCell']}>
                                            <Text
                                                component="span"
                                                size="sm"
                                                c="dimmed"
                                                aria-hidden="true"
                                                className={classes['ratePrefixSpacer']}
                                            >
                                                $
                                            </Text>
                                            <NumberInput
                                                size="sm"
                                                aria-label="Line percentage rate"
                                                min={0}
                                                decimalScale={2}
                                                value={line.rate}
                                                readOnly={readOnly}
                                                onChange={(value) =>
                                                    onLinesChange(
                                                        updateLine(lines, line.id, {
                                                            rate:
                                                                typeof value === 'number'
                                                                    ? value
                                                                    : 0,
                                                        }),
                                                    )
                                                }
                                                classNames={{root: classes['rateInput']}}
                                            />
                                            <Text size="sm" c="dimmed">
                                                %
                                            </Text>
                                        </div>
                                    </>
                                )}

                                <div className={classes['basisCell']}>
                                    {line.kind === 'item' && periodBasis != null && (
                                        <QuoteLinePeriodBasis
                                            basis={periodBasis}
                                            checked={line.forRentalPeriod ?? false}
                                            disabled={readOnly}
                                            onChange={(forRentalPeriod) =>
                                                onLinesChange(
                                                    updateLine(lines, line.id, {
                                                        forRentalPeriod,
                                                        duration: null,
                                                    }),
                                                )
                                            }
                                        />
                                    )}
                                </div>

                                <Text size="sm" fw={500} className={classes['extAmount']}>
                                    {formatPrice(extended)}
                                </Text>

                                {readOnly ? (
                                    <span aria-hidden="true" />
                                ) : (
                                    <ActionIcon
                                        variant="subtle"
                                        color="gray"
                                        size="sm"
                                        aria-label="Remove line"
                                        onClick={() => onLinesChange(removeLine(lines, line.id))}
                                    >
                                        <Text span size="sm" c="dimmed" lh={1}>
                                            ×
                                        </Text>
                                    </ActionIcon>
                                )}
                            </div>
                        )
                    })}
                </Box>

                <Group justify="space-between" align="center">
                    {readOnly ? (
                        <span />
                    ) : (
                        <Group gap="xs">
                            <Menu position="bottom-start" withinPortal>
                                <Menu.Target>
                                    <Button
                                        variant="filled"
                                        color="brand"
                                        size="sm"
                                        leftSection={<IconPlus size={16} />}
                                        rightSection={<IconChevronDown size={14} stroke={1.5} />}
                                    >
                                        Add common charge
                                    </Button>
                                </Menu.Target>
                                <Menu.Dropdown>
                                    <Menu.Label>Common charges</Menu.Label>
                                    {CHARGE_TYPE_MENU_ITEMS.map((item) => {
                                        const {chargeType, label, hint} = item
                                        const available = chargeTypeAvailable(lines, chargeType)

                                        return (
                                            <Menu.Item
                                                key={label}
                                                disabled={!available}
                                                onClick={() => addCommonCharge(item)}
                                                rightSection={
                                                    <Text size="xs" c="dimmed">
                                                        {available ? hint : 'Already added'}
                                                    </Text>
                                                }
                                            >
                                                {label}
                                            </Menu.Item>
                                        )
                                    })}
                                </Menu.Dropdown>
                            </Menu>

                            <Button
                                variant="light"
                                size="sm"
                                leftSection={<IconPlus size={16} />}
                                onClick={handleAddItemLine}
                            >
                                Add item
                            </Button>
                            <Button
                                variant="light"
                                size="sm"
                                leftSection={<IconPlus size={16} />}
                                onClick={handleAddPercentLine}
                            >
                                Add %
                            </Button>
                        </Group>
                    )}

                    <Stack gap={4} align="flex-end">
                        <Group gap="md">
                            <Text size="sm" c="dimmed">
                                Subtotal
                            </Text>
                            <Text size="sm" fw={500} className={classes['extAmount']}>
                                {formatPrice(subtotal)}
                            </Text>
                        </Group>
                        <Group gap="md">
                            <Text size="sm" fw={600}>
                                Total
                            </Text>
                            <Text size="md" fw={700} className={classes['extAmount']}>
                                {formatPrice(subtotal)}
                            </Text>
                        </Group>
                    </Stack>
                </Group>
            </Stack>
        </Paper>
    )
}
